import "./app.scss";
import { useContext, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundaryComponent } from "@flexfront/ui/react";
import { useDebug } from "./common/hooks/useDebug";
import { ProtectedRoute, ProtectedRouteProps } from "./common/guards/protected-route";
import { LoginComponent } from "./common/components/login-component/login-component";
import { useAppSelector } from "./common/store/hooks";
import { selectAuth } from "./common/store/auth/auth.slice";
import { selectIsTrackingEnabled } from "./common/store/tenant-config/tenant-config.slice";
import { EnsureTenantConfig } from "./common/guards/ensure-tenant-config";
import { MatomoContext, useMatomo } from "@jonkoops/matomo-tracker-react";
import { DashboardSelectorComponent } from "./common/components/dashboard-selector-component/dashboard-selector-component";
import { ReportComponent } from "./default/components/report-component/report-component";
import { DashboardProvider } from "./common/providers/dashboard-provider";
import { IFrameComponent } from "./default/components/iframe-component/iframe-component";
import NotFoundComponent from "./common/components/not-found-component/not-found-component";
import { LabelsProvider } from "./common/providers/labels-provider";
import { FontAwesomeIconEnhancer } from "./common/components/font-awesome-icon-enhancer/FontAwesomeIconEnhancer";
import { ReportLoadingComponent } from "./default/components/report-loading-component/report-loading-component";

// Registers Matomo outbound-link tracking. useMatomo().enableLinkTracking()
// internally calls a hook (useOutboundClickListener), so it must run during
// render with a guaranteed instance. This component is rendered only once a
// tracking-enabled Matomo instance exists, which keeps hook order stable.
function OutboundLinkTracker() {
  const { enableLinkTracking } = useMatomo();
  enableLinkTracking();
  return null;
}

export function App() {
  useDebug();

  const auth = useAppSelector(selectAuth);

  const { trackPageView } = useMatomo();
  const matomoInstance = useContext(MatomoContext);
  const isTrackingEnabled = useAppSelector(selectIsTrackingEnabled);
  const hasInitialisedTracking = useRef(false);

  // The Matomo tracker is created lazily once the tenant is resolved (see
  // MatomoProviderGate), so on the first render the instance is still null and
  // every tracking call is a no-op. Fire the initial page view once the instance
  // exists and the tenant allows tracking. For tracking-disabled tenants this
  // never runs and, because the tracker is built with `disabled: true`, no request
  // is ever sent. (Outbound-link tracking is handled by OutboundLinkTracker.)
  useEffect(() => {
    if (matomoInstance && isTrackingEnabled && !hasInitialisedTracking.current) {
      trackPageView();
      hasInitialisedTracking.current = true;
    }
  }, [matomoInstance, isTrackingEnabled, trackPageView]);

  const dashboard = <main className="main">
    <EnsureTenantConfig>
      <>
        <FontAwesomeIconEnhancer />
        <LabelsProvider>
          <DashboardSelectorComponent />
        </LabelsProvider>
      </>
    </EnsureTenantConfig>
  </main>;

  const report = <main className="main">
    <EnsureTenantConfig>
      <>
        <FontAwesomeIconEnhancer />
        <LabelsProvider>
          <DashboardProvider
            disableShortfallEliminationCall={true}>
            <ReportComponent />
          </DashboardProvider>
        </LabelsProvider>
      </>
    </EnsureTenantConfig>
  </main>;

  const reportLoading = <main className="main">
    <EnsureTenantConfig>
      <>
        <FontAwesomeIconEnhancer />
        <LabelsProvider>
          <ReportLoadingComponent />
        </LabelsProvider>
      </>
    </EnsureTenantConfig>
  </main>;

  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    isAuthenticated: auth.isLoggedIn,
    authenticationPath: "login",
  };

  return (
    <ErrorBoundaryComponent>
      {matomoInstance && isTrackingEnabled && <OutboundLinkTracker />}
      <Routes>
        <Route path="/">
          <Route path="" element={dashboard} />
          <Route path=":name" element={dashboard} />
          <Route path=":name/iframe" element={<IFrameComponent />} />
          <Route path="report-loading" element={reportLoading} />
          <Route path=":name/report-loading" element={reportLoading} />
          <Route path=":name/report/:state" element={report} />
          <Route path="/persona/:id" element={dashboard} />
          <Route path=":name/persona/:id" element={dashboard} />
          <Route
            path=":name/config"
            element={<ProtectedRoute {...defaultProtectedRouteProps} outlet={dashboard} />}
          />
          <Route path="/config" element={<ProtectedRoute {...defaultProtectedRouteProps} outlet={dashboard} />} />
          <Route path="/list" element={<ProtectedRoute {...defaultProtectedRouteProps} outlet={dashboard} />} />
        </Route>
        <Route path="/config/login" element={<LoginComponent />} />
        <Route path="/list/login" element={<LoginComponent />} />
        <Route path="/:name/config/login" element={<LoginComponent />} />
        <Route path="*" element={<EnsureTenantConfig>
            <LabelsProvider>
              <NotFoundComponent />
            </LabelsProvider>
          </EnsureTenantConfig>} />
      </Routes>
    </ErrorBoundaryComponent>
  );
}

export default App;
